import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Link2, Link2Off, MapPin, Radar, RefreshCw } from 'lucide-react'
import { artistFetch } from '@/lib/artist-api'
import { ApiClientError } from '@/lib/api-client'
import { useAuthStore } from '@/lib/auth-store'
import { PageHeader } from '@/components/PageHeader'

interface HiveCellsStatus {
  connected: boolean
  cellSlug?: string
  cellUrl?: string
  hiveCellsOrgId?: string | null
}

interface ConnectBody {
  apiKey: string
  signingSecret: string
  cellSlug: string
  baseUrl: string
  hiveCellsOrgId?: string
}

const HIVECELLS_BASE_URL =
  import.meta.env.VITE_HIVECELLS_BASE_URL ?? 'https://hivecells.geekybee.net/api/v1'

export function IntegrationsPage() {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    apiKey: '',
    signingSecret: '',
    cellSlug: '',
    hiveCellsOrgId: '',
  })
  const [error, setError] = useState<string | null>(null)

  const statusQ = useQuery({
    queryKey: ['integrations', 'hive-cells'],
    queryFn: () => artistFetch<HiveCellsStatus>('/integrations/hive-cells'),
  })

  const connectM = useMutation({
    mutationFn: (body: typeof form) => {
      const payload: ConnectBody = {
        apiKey: body.apiKey,
        signingSecret: body.signingSecret,
        cellSlug: body.cellSlug,
        baseUrl: HIVECELLS_BASE_URL,
        ...(body.hiveCellsOrgId ? { hiveCellsOrgId: body.hiveCellsOrgId } : {}),
      }
      return artistFetch('/integrations/hive-cells/connect', {
        method: 'POST',
        body: payload,
      })
    },
    onSuccess: () => {
      setError(null)
      setForm({ apiKey: '', signingSecret: '', cellSlug: '', hiveCellsOrgId: '' })
      void qc.invalidateQueries({ queryKey: ['integrations', 'hive-cells'] })
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiClientError ? e.message : 'Connection failed')
    },
  })

  const disconnectM = useMutation({
    mutationFn: () => artistFetch('/integrations/hive-cells', { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations', 'hive-cells'] })
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiClientError ? e.message : 'Disconnect failed')
    },
  })

  const s = statusQ.data

  return (
    <div>
      <PageHeader
        title="Integrations"
        subtitle="Connect Ovation to external platforms."
      />

      <div className="max-w-2xl space-y-6">
        <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
          <h2 className="font-display text-xl">Social media, Spotify &amp; Chartmetric</h2>
          <p className="mt-1 text-sm text-ovation-muted">
            OAuth and API connections for Spotify, Chartmetric, Facebook, Instagram, YouTube,
            Google Analytics, and Search Console live on the Social &amp; streaming page.
          </p>
          <ul className="mt-3 flex flex-wrap gap-2 text-xs text-ovation-muted">
            {['Spotify', 'Chartmetric', 'Facebook', 'Instagram', 'YouTube', 'TikTok', 'GA4', 'Search Console'].map(
              (label) => (
                <li
                  key={label}
                  className="rounded-full border border-ovation-ink/10 bg-ovation-ink/5 px-2.5 py-1"
                >
                  {label}
                </li>
              ),
            )}
          </ul>
          <Link
            to="/audience"
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Radar className="h-3.5 w-3.5" />
            Open platform connections
          </Link>
        </div>

        <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
          <h2 className="font-display text-xl">Brokers, coverage &amp; ticket sales</h2>
          <p className="mt-1 text-sm text-ovation-muted">
            Monitor booking agents, territory coverage, tier pre-releases, and ticket sold/held counts.
          </p>
          <Link
            to="/marketing"
            className="mt-4 inline-flex items-center gap-2 rounded-md border border-ovation-ink/15 px-4 py-2 text-sm font-medium hover:bg-ovation-ink/5"
          >
            Open marketing hub
          </Link>
        </div>

        <ShopifySection />
        <WooCommerceSection />

        {/* ── Hive Cells ── */}
        <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h2 className="font-display text-xl">Hive Cells</h2>
              <p className="mt-0.5 text-sm text-ovation-muted">
                Publish confirmed shows to your fan community. Purchase, scan, and tier events feed fan geography on the audience map.
              </p>
            </div>
            {s?.connected && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-800">
                <Link2 className="h-3 w-3" />
                Connected
              </span>
            )}
          </div>

          {statusQ.isLoading ? (
            <p className="text-sm text-ovation-muted">Checking connection status…</p>
          ) : s?.connected ? (
            <div className="space-y-4">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-ovation-muted">Cell</dt>
                  <dd className="font-mono">{s.cellSlug}</dd>
                </div>
                {s.hiveCellsOrgId && (
                  <div className="flex justify-between">
                    <dt className="text-ovation-muted">Org UUID</dt>
                    <dd className="font-mono text-xs">{s.hiveCellsOrgId}</dd>
                  </div>
                )}
              </dl>

              {error && (
                <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                {s.cellUrl && (
                  <a
                    href={s.cellUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View Hive Cell
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Disconnect Hive Cells? Show syncing will stop immediately.')) {
                      disconnectM.mutate()
                    }
                  }}
                  disabled={disconnectM.isPending}
                  className="inline-flex items-center gap-2 rounded-md border border-ovation-ink/15 px-4 py-2 text-sm hover:bg-ovation-ink/5 disabled:opacity-50"
                >
                  <Link2Off className="h-3.5 w-3.5" />
                  {disconnectM.isPending ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                connectM.mutate(form)
              }}
              className="space-y-3"
            >
              <div>
                <label htmlFor="hc-cell-slug" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                  Cell slug
                </label>
                <input
                  id="hc-cell-slug"
                  required
                  placeholder="e.g. smokeyrobinson"
                  className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
                  value={form.cellSlug}
                  onChange={(e) => setForm({ ...form, cellSlug: e.target.value })}
                />
              </div>
              <div>
                <label htmlFor="hc-api-key" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                  API key
                </label>
                <input
                  id="hc-api-key"
                  type="password"
                  required
                  placeholder="hc_ov_live_…"
                  className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 font-mono text-sm"
                  value={form.apiKey}
                  onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                />
              </div>
              <div>
                <label htmlFor="hc-signing-secret" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                  Signing secret
                </label>
                <input
                  id="hc-signing-secret"
                  type="password"
                  required
                  placeholder="Webhook signing secret"
                  className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 font-mono text-sm"
                  value={form.signingSecret}
                  onChange={(e) => setForm({ ...form, signingSecret: e.target.value })}
                />
              </div>
              <div>
                <label htmlFor="hc-org-id" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                  Organisation UUID{' '}
                  <span className="normal-case font-normal text-ovation-muted/70">(optional — auto-populated on first webhook)</span>
                </label>
                <input
                  id="hc-org-id"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 font-mono text-sm"
                  value={form.hiveCellsOrgId}
                  onChange={(e) => setForm({ ...form, hiveCellsOrgId: e.target.value })}
                />
              </div>

              {error && (
                <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={connectM.isPending}
                className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
              >
                {connectM.isPending ? 'Validating…' : 'Connect'}
              </button>
            </form>
          )}
        </div>

        <VelvetRopeSection />
      </div>
    </div>
  )
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '')

interface ShopifyStatus {
  connected: boolean
  shopDomain?: string
  shopName?: string
}

function ShopifySection() {
  const qc = useQueryClient()
  const [form, setForm] = useState({ shopDomain: '', accessToken: '', webhookSecret: '' })
  const [error, setError] = useState<string | null>(null)

  const statusQ = useQuery({
    queryKey: ['integrations', 'shopify'],
    queryFn: () => artistFetch<ShopifyStatus>('/integrations/shopify'),
  })

  const connectM = useMutation({
    mutationFn: (body: typeof form) =>
      artistFetch('/integrations/shopify/connect', {
        method: 'POST',
        body: {
          shopDomain: body.shopDomain.trim(),
          accessToken: body.accessToken.trim(),
          ...(body.webhookSecret.trim() ? { webhookSecret: body.webhookSecret.trim() } : {}),
        },
      }),
    onSuccess: () => {
      setError(null)
      setForm({ shopDomain: '', accessToken: '', webhookSecret: '' })
      void qc.invalidateQueries({ queryKey: ['integrations', 'shopify'] })
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiClientError ? e.message : 'Connection failed')
    },
  })

  const disconnectM = useMutation({
    mutationFn: () => artistFetch('/integrations/shopify', { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['integrations', 'shopify'] }),
    onError: (e: unknown) => {
      setError(e instanceof ApiClientError ? e.message : 'Disconnect failed')
    },
  })

  const s = statusQ.data
  const webhookUrl = `${API_BASE}/webhooks/shopify`

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="font-display text-xl">Shopify</h2>
          <p className="mt-0.5 text-sm text-ovation-muted">
            Import online merch orders with shipping location for fan economics mapping.
          </p>
        </div>
        {s?.connected && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-800">
            <Link2 className="h-3 w-3" />
            Connected
          </span>
        )}
      </div>

      {statusQ.isLoading ? (
        <p className="text-sm text-ovation-muted">Checking connection status…</p>
      ) : s?.connected ? (
        <div className="space-y-4">
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-ovation-muted">Shop</dt>
              <dd className="font-mono">{s.shopName ?? s.shopDomain}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-ovation-muted">Domain</dt>
              <dd className="font-mono text-xs">{s.shopDomain}</dd>
            </div>
          </dl>
          <div className="rounded-md bg-ovation-ink/5 px-3 py-2 text-xs text-ovation-muted">
            <p className="font-semibold text-ovation-ink">Webhook URL (order create/update)</p>
            <code className="mt-1 block break-all">{webhookUrl}</code>
            <p className="mt-2">Subscribe to <strong>orders/create</strong> and <strong>orders/updated</strong>.</p>
          </div>
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          <button
            type="button"
            onClick={() => {
              if (confirm('Disconnect Shopify? New orders will not be imported.')) disconnectM.mutate()
            }}
            disabled={disconnectM.isPending}
            className="inline-flex items-center gap-2 rounded-md border border-ovation-ink/15 px-4 py-2 text-sm hover:bg-ovation-ink/5 disabled:opacity-50"
          >
            <Link2Off className="h-3.5 w-3.5" />
            Disconnect
          </button>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            connectM.mutate(form)
          }}
          className="space-y-3"
        >
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
              Shop domain
            </label>
            <input
              required
              placeholder="your-store.myshopify.com"
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
              value={form.shopDomain}
              onChange={(e) => setForm({ ...form, shopDomain: e.target.value })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
              Admin API access token
            </label>
            <input
              required
              type="password"
              placeholder="shpat_…"
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 font-mono text-sm"
              value={form.accessToken}
              onChange={(e) => setForm({ ...form, accessToken: e.target.value })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
              Webhook signing secret{' '}
              <span className="normal-case font-normal text-ovation-muted/70">(optional)</span>
            </label>
            <input
              type="password"
              placeholder="From Shopify webhook settings"
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 font-mono text-sm"
              value={form.webhookSecret}
              onChange={(e) => setForm({ ...form, webhookSecret: e.target.value })}
            />
          </div>
          <p className="text-xs text-ovation-muted">
            Ovation auto-registers the <code>orders/paid</code> webhook when{' '}
            <code>API_BASE_URL</code> is set on the server. Manual fallback:
            <code className="mt-1 block break-all">{webhookUrl}</code>
          </p>
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          <button
            type="submit"
            disabled={connectM.isPending}
            className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            {connectM.isPending ? 'Validating…' : 'Connect Shopify'}
          </button>
        </form>
      )}
    </div>
  )
}

interface WooCommerceStatus {
  connected: boolean
  siteUrl?: string
  siteName?: string
}

function WooCommerceSection() {
  const qc = useQueryClient()
  const artistId = useAuthStore((s) => s.user?.artistId)
  const [form, setForm] = useState({ siteUrl: '', consumerKey: '', consumerSecret: '' })
  const [error, setError] = useState<string | null>(null)

  const statusQ = useQuery({
    queryKey: ['integrations', 'woocommerce'],
    queryFn: () => artistFetch<WooCommerceStatus>('/integrations/woocommerce'),
  })

  const connectM = useMutation({
    mutationFn: (body: typeof form) =>
      artistFetch('/integrations/woocommerce/connect', {
        method: 'POST',
        body: {
          siteUrl: body.siteUrl.trim(),
          consumerKey: body.consumerKey.trim(),
          consumerSecret: body.consumerSecret.trim(),
        },
      }),
    onSuccess: () => {
      setError(null)
      setForm({ siteUrl: '', consumerKey: '', consumerSecret: '' })
      void qc.invalidateQueries({ queryKey: ['integrations', 'woocommerce'] })
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiClientError ? e.message : 'Connection failed')
    },
  })

  const disconnectM = useMutation({
    mutationFn: () => artistFetch('/integrations/woocommerce', { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['integrations', 'woocommerce'] }),
    onError: (e: unknown) => {
      setError(e instanceof ApiClientError ? e.message : 'Disconnect failed')
    },
  })

  const s = statusQ.data
  const webhookUrl =
    artistId ? `${API_BASE}/webhooks/woocommerce?artistId=${artistId}` : null

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="font-display text-xl">WooCommerce</h2>
          <p className="mt-0.5 text-sm text-ovation-muted">
            Import WooCommerce orders with customer shipping location for revenue by region.
          </p>
        </div>
        {s?.connected && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-800">
            <Link2 className="h-3 w-3" />
            Connected
          </span>
        )}
      </div>

      {statusQ.isLoading ? (
        <p className="text-sm text-ovation-muted">Checking connection status…</p>
      ) : s?.connected ? (
        <div className="space-y-4">
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-ovation-muted">Site</dt>
              <dd className="font-mono text-xs">{s.siteName ?? s.siteUrl}</dd>
            </div>
          </dl>
          {webhookUrl && (
            <div className="rounded-md bg-ovation-ink/5 px-3 py-2 text-xs text-ovation-muted">
              <p className="font-semibold text-ovation-ink">Webhook URL (order updated)</p>
              <code className="mt-1 block break-all">{webhookUrl}</code>
              <p className="mt-2">Topic: <strong>order.updated</strong> — include the artist ID query param.</p>
            </div>
          )}
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          <button
            type="button"
            onClick={() => {
              if (confirm('Disconnect WooCommerce? New orders will not be imported.')) disconnectM.mutate()
            }}
            disabled={disconnectM.isPending}
            className="inline-flex items-center gap-2 rounded-md border border-ovation-ink/15 px-4 py-2 text-sm hover:bg-ovation-ink/5 disabled:opacity-50"
          >
            <Link2Off className="h-3.5 w-3.5" />
            Disconnect
          </button>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            connectM.mutate(form)
          }}
          className="space-y-3"
        >
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
              Site URL
            </label>
            <input
              required
              type="url"
              placeholder="https://yourstore.com"
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
              value={form.siteUrl}
              onChange={(e) => setForm({ ...form, siteUrl: e.target.value })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
              Consumer key
            </label>
            <input
              required
              type="password"
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 font-mono text-sm"
              value={form.consumerKey}
              onChange={(e) => setForm({ ...form, consumerKey: e.target.value })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
              Consumer secret
            </label>
            <input
              required
              type="password"
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 font-mono text-sm"
              value={form.consumerSecret}
              onChange={(e) => setForm({ ...form, consumerSecret: e.target.value })}
            />
          </div>
          {webhookUrl && (
            <p className="text-xs text-ovation-muted">
              Webhook URL after connect: <code className="break-all">{webhookUrl}</code>
            </p>
          )}
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          <button
            type="submit"
            disabled={connectM.isPending}
            className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            {connectM.isPending ? 'Validating…' : 'Connect WooCommerce'}
          </button>
        </form>
      )}
    </div>
  )
}

interface VelvetRopeStatus {
  connected: boolean
  tenantSlug?: string
  apiBaseUrl?: string | null
  connectionMode?: 'api' | 'database' | null
  defaultApiBaseUrl?: string
}

interface FanDemandSummary {
  velvetRopeMapped: boolean
  velvetRopeTenantSlug: string | null
  connectionMode: 'api' | 'database' | null
  apiBaseUrl: string | null
  syncAvailable: boolean
  platformConfigured: boolean
  lastSyncedAt: string | null
  lastError: string | null
  totalRequests: number
  cityCount: number
}

interface VelvetRopeConnectInput {
  tenantSlug: string
  apiBaseUrl: string
  integrationToken: string
}

interface FanDemandSyncResult {
  ok: boolean
  result: {
    fetched: number
    imported: number
    skippedNotTourDemand: number
    skippedNoLocation: number
    skippedDuplicate: number
    skippedGeocode: number
    errors: number
  }
}

function VelvetRopeSection() {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const defaultSlug =
    user?.artistMemberships.find((m) => m.artistId === user?.artistId)?.artistSlug ?? ''
  const [tenantSlug, setTenantSlug] = useState(defaultSlug)
  const [apiBaseUrl, setApiBaseUrl] = useState('https://velvet-rope.geekybee.net')
  const [integrationToken, setIntegrationToken] = useState('')
  const [vrError, setVrError] = useState<string | null>(null)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)

  useEffect(() => {
    setTenantSlug(defaultSlug)
  }, [defaultSlug])

  const statusQ = useQuery({
    queryKey: ['integrations', 'velvet-rope'],
    queryFn: () => artistFetch<VelvetRopeStatus>('/integrations/velvet-rope'),
  })

  useEffect(() => {
    if (statusQ.data?.defaultApiBaseUrl && !statusQ.data.connected) {
      setApiBaseUrl(statusQ.data.defaultApiBaseUrl)
    }
  }, [statusQ.data?.defaultApiBaseUrl, statusQ.data?.connected])

  const summaryQ = useQuery({
    queryKey: ['fan-demand-summary'],
    queryFn: () => artistFetch<FanDemandSummary>('/fan-demand/summary'),
    enabled: statusQ.data?.connected === true,
  })

  const syncM = useMutation({
    mutationFn: () =>
      artistFetch<FanDemandSyncResult>('/fan-demand/sync', { method: 'POST' }),
    onSuccess: (data) => {
      setVrError(null)
      const r = data.result
      setSyncMessage(
        `Synced: ${r.imported} new pin${r.imported === 1 ? '' : 's'} from ${r.fetched} VR comment${r.fetched === 1 ? '' : 's'}`
        + (r.skippedDuplicate ? ` (${r.skippedDuplicate} already imported)` : ''),
      )
      void qc.invalidateQueries({ queryKey: ['fan-demand-summary'] })
      void qc.invalidateQueries({ queryKey: ['fan-demand-map'] })
      void qc.invalidateQueries({ queryKey: ['territory-map'] })
      void qc.invalidateQueries({ queryKey: ['territory-map-fan-demand'] })
    },
    onError: (e: unknown) => {
      setVrError(e instanceof ApiClientError ? e.message : 'Sync failed')
    },
  })

  const connectM = useMutation({
    mutationFn: (input: VelvetRopeConnectInput) =>
      artistFetch('/integrations/velvet-rope/connect', {
        method: 'POST',
        body: input,
      }),
    onSuccess: async () => {
      setVrError(null)
      setIntegrationToken('')
      await qc.invalidateQueries({ queryKey: ['integrations', 'velvet-rope'] })
      syncM.mutate()
    },
    onError: (e: unknown) => {
      setVrError(e instanceof ApiClientError ? e.message : 'Connection failed')
    },
  })

  const disconnectM = useMutation({
    mutationFn: () => artistFetch('/integrations/velvet-rope', { method: 'DELETE' }),
    onSuccess: () => {
      setSyncMessage(null)
      void qc.invalidateQueries({ queryKey: ['integrations', 'velvet-rope'] })
      void qc.invalidateQueries({ queryKey: ['fan-demand-summary'] })
    },
    onError: (e: unknown) => {
      setVrError(e instanceof ApiClientError ? e.message : 'Disconnect failed')
    },
  })

  const connected = statusQ.data?.connected === true
  const summary = summaryQ.data

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="font-display text-xl">Velvet Rope</h2>
          <p className="mt-0.5 text-sm text-ovation-muted">
            Import auto-approved fan comments asking for concerts in their city — shown as blue
            counted pins on the{' '}
            <Link to="/dashboard?layers=fan_demand" className="text-ovation-ink hover:underline">
              Territory map
            </Link>
            .
          </p>
        </div>
        {connected && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-800">
            <Link2 className="h-3 w-3" />
            Connected
          </span>
        )}
      </div>

      {summary && !summary.syncAvailable && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Fan-demand sync is not available for this connection. Reconnect with a valid Velvet
          Rope integration token, or ask your admin to configure legacy{' '}
          <code className="text-xs">VELVETROPE_*</code> env vars on the API.
        </div>
      )}

      {statusQ.isLoading ? (
        <p className="text-sm text-ovation-muted">Checking connection status…</p>
      ) : connected ? (
        <div className="space-y-4">
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-ovation-muted">Tenant slug</dt>
              <dd className="font-mono">{statusQ.data?.tenantSlug ?? summary?.velvetRopeTenantSlug}</dd>
            </div>
            {statusQ.data?.connectionMode && (
              <div className="flex justify-between gap-4">
                <dt className="text-ovation-muted">Connection</dt>
                <dd className="font-mono text-xs">
                  {statusQ.data.connectionMode === 'api' ? 'HTTP API' : 'Legacy database'}
                </dd>
              </div>
            )}
            {statusQ.data?.apiBaseUrl && (
              <div className="flex justify-between gap-4">
                <dt className="text-ovation-muted">API URL</dt>
                <dd className="truncate font-mono text-xs">{statusQ.data.apiBaseUrl}</dd>
              </div>
            )}
            {summary && (
              <>
                <div className="flex justify-between gap-4">
                  <dt className="text-ovation-muted">Map pins</dt>
                  <dd>
                    <strong>{summary.totalRequests}</strong> requests across{' '}
                    <strong>{summary.cityCount}</strong> cities
                  </dd>
                </div>
                {summary.lastSyncedAt && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-ovation-muted">Last synced</dt>
                    <dd>{new Date(summary.lastSyncedAt).toLocaleString()}</dd>
                  </div>
                )}
                {summary.lastError && (
                  <div className="rounded-md bg-red-50 px-3 py-2 text-red-700">
                    Last sync error: {summary.lastError}
                  </div>
                )}
              </>
            )}
          </dl>

          {syncMessage && (
            <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {syncMessage}
            </div>
          )}
          {vrError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{vrError}</div>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => syncM.mutate()}
              disabled={syncM.isPending || summary?.syncAvailable === false}
              className="inline-flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${syncM.isPending ? 'animate-spin' : ''}`} />
              {syncM.isPending ? 'Syncing…' : 'Sync fan requests now'}
            </button>
            <Link
              to="/dashboard?layers=fan_demand"
              className="inline-flex items-center gap-2 rounded-md border border-ovation-ink/15 px-4 py-2 text-sm hover:bg-ovation-ink/5"
            >
              <MapPin className="h-3.5 w-3.5" />
              View on map
            </Link>
            <button
              type="button"
              onClick={() => {
                if (confirm('Disconnect Velvet Rope? Fan-demand pins will stop updating.')) {
                  disconnectM.mutate()
                }
              }}
              disabled={disconnectM.isPending}
              className="inline-flex items-center gap-2 rounded-md border border-ovation-ink/15 px-4 py-2 text-sm hover:bg-ovation-ink/5 disabled:opacity-50"
            >
              <Link2Off className="h-3.5 w-3.5" />
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            connectM.mutate({
              tenantSlug: tenantSlug.trim(),
              apiBaseUrl: apiBaseUrl.trim(),
              integrationToken: integrationToken.trim(),
            })
          }}
          className="space-y-3"
        >
          <div>
            <label
              htmlFor="vr-tenant-slug"
              className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted"
            >
              Velvet Rope tenant slug
            </label>
            <input
              id="vr-tenant-slug"
              required
              placeholder="e.g. smokey-robinson"
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 font-mono text-sm"
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
            />
            <p className="mt-1 text-xs text-ovation-muted">
              Must match the active tenant slug in Velvet Rope (usually the same as this artist&apos;s
              Ovation slug).
            </p>
          </div>

          <div>
            <label
              htmlFor="vr-api-url"
              className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted"
            >
              Velvet Rope API URL
            </label>
            <input
              id="vr-api-url"
              required
              type="url"
              placeholder="https://velvet-rope.geekybee.net"
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 font-mono text-sm"
              value={apiBaseUrl}
              onChange={(e) => setApiBaseUrl(e.target.value)}
            />
          </div>

          <div>
            <label
              htmlFor="vr-integration-token"
              className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted"
            >
              Integration token
            </label>
            <input
              id="vr-integration-token"
              required
              type="password"
              autoComplete="off"
              placeholder="vr_int_…"
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 font-mono text-sm"
              value={integrationToken}
              onChange={(e) => setIntegrationToken(e.target.value)}
            />
            <p className="mt-1 text-xs text-ovation-muted">
              Mint a per-tenant token in Velvet Rope via{' '}
              <code className="text-[11px]">POST /api/admin/ovation-integration-token</code>{' '}
              (requires <code className="text-[11px]">ADMIN_API_KEY</code>). The token is shown once
              — paste it here. Ovation verifies it against{' '}
              <code className="text-[11px]">GET /api/integrations/v1/me</code> before saving.
            </p>
          </div>

          {vrError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{vrError}</div>
          )}

          <button
            type="submit"
            disabled={
              connectM.isPending
              || !tenantSlug.trim()
              || !apiBaseUrl.trim()
              || !integrationToken.trim()
            }
            className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            {connectM.isPending ? 'Connecting…' : 'Connect & sync'}
          </button>
        </form>
      )}
    </div>
  )
}
